import React, { useState } from "react";
import { gql, useQuery } from "@apollo/client";

import { makeStyles } from "@material-ui/core/styles";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import ListItemText from "@material-ui/core/ListItemText";
import Select from "@material-ui/core/Select";
import Checkbox from "@material-ui/core/Checkbox";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 300,
    maxWidth: 500,
    verticalAlign: "bottom"
  },
  narrowFormControl: {
    margin: theme.spacing(1),
    minWidth: 143,
    maxWidth: 400,
    verticalAlign: "bottom"
  }
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const GET_LOOKUPS = gql`
  query {
    QuestionSet(orderBy: name_asc) {
      id
      name
    }
    Difficulty(orderBy: name_asc) {
      id
      name
    }
    TournamentYears(orderBy: year_asc) {
      year
    }
  }
`;

export default function Filters({ filters, onFiltersUpdate, hideWinLoss }) {
  const classes = useStyles();
  const { loading, data } = useQuery(GET_LOOKUPS, {
    variables: {}
  });
  const [difficultyInputValue, setDifficultyInputValue] = useState("");
  const [qsInputValue, setQSInputValue] = useState("");

  const handleDifficultyChange = (_, value) => {
    var newFilters = {
      ...filters,
      difficulties: value.map(v => v.id)
    };

    onFiltersUpdate(newFilters);
  };

  const handleQuestionSetChange = (_, value) => {
    var newFilters = {
      ...filters,
      questionSets: value.map(v => v.id)
    };

    onFiltersUpdate(newFilters);
  };

  const handleYearChange = event => {
    var newFilters = {
      ...filters,
      years: event.target.value
    };

    onFiltersUpdate(newFilters);
  };

  const handleWinLossChange = event => {
    var newFilters = {
      ...filters,
      winLoss: event.target.value
    };

    onFiltersUpdate(newFilters);
  };

  const handleTournamentTypeChange = event => {
    var newFilters = {
      ...filters,
      tournamentType: event.target.value
    };

    onFiltersUpdate(newFilters);
  };

  const handleSetTypeChange = event => {
    var newFilters = {
      ...filters,
      setType: event.target.value
    };

    onFiltersUpdate(newFilters);
  };

  return (
    <div>
      <div>
        <FormControl className={classes.formControl}>
          <Autocomplete
            id="difficulty"
            multiple
            limitTags={2}
            disableCloseOnSelect
            inputValue={difficultyInputValue}
            value={
              !loading
                ? data.Difficulty.filter(s =>
                    (filters.difficulties || []).includes(s.id)
                  )
                : []
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Difficulty"
                onChange={e => setDifficultyInputValue(e.target.value)}
              />
            )}
            onClose={() => setDifficultyInputValue("")}
            onChange={handleDifficultyChange}
            getOptionLabel={option => option.name}
            options={!loading ? data.Difficulty : []}
            renderOption={option => (
              <React.Fragment>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={
                    filters.difficulties
                      ? filters.difficulties.indexOf(option.id) > -1
                      : false
                  }
                />
                {option.name}
              </React.Fragment>
            )}
          />
        </FormControl>
        <FormControl className={classes.formControl}>
          <Autocomplete
            id="questionSet"
            multiple
            limitTags={2}
            disableCloseOnSelect
            inputValue={qsInputValue}
            value={
              !loading
                ? data.QuestionSet.filter(s =>
                    (filters.questionSets || []).includes(s.id)
                  )
                : []
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Question Set"
                onChange={e => setQSInputValue(e.target.value)}
              />
            )}
            onClose={() => setQSInputValue("")}
            onChange={handleQuestionSetChange}
            getOptionLabel={option => option.name}
            options={!loading ? data.QuestionSet : []}
            renderOption={option => (
              <React.Fragment>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={
                    filters.questionSets
                      ? filters.questionSets.indexOf(option.id) > -1
                      : false
                  }
                />
                {option.name}
              </React.Fragment>
            )}
          />
        </FormControl>
        {!hideWinLoss && (
          <FormControl className={classes.narrowFormControl}>
            <InputLabel id="winLoss">Win/Loss</InputLabel>
            <Select
              labelId="winLoss"
              id="winLoss"
              value={
                !!filters.winLoss === filters.winLoss ? filters.winLoss : ""
              }
              onChange={handleWinLossChange}
            >
              <MenuItem value={""}></MenuItem>
              <MenuItem value={true}>In wins</MenuItem>
              <MenuItem value={false}>In losses</MenuItem>
            </Select>
          </FormControl>
        )}
      </div>
      <div>
        <FormControl className={classes.formControl}>
          <InputLabel id="year">Season</InputLabel>
          <Select
            labelId="year"
            id="year"
            multiple
            value={filters.years || []}
            onChange={handleYearChange}
            input={<Input />}
            renderValue={selected =>
              selected.map(year => `${year}-${year + 1}`).join(", ")
            }
            MenuProps={MenuProps}
          >
            {!loading &&
              data.TournamentYears.map(({ year }) => (
                <MenuItem key={year} value={year}>
                  <Checkbox
                    checked={
                      filters.years ? filters.years.indexOf(year) > -1 : false
                    }
                  />
                  <ListItemText primary={`${year}-${year + 1}`} />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl className={classes.narrowFormControl}>
          <InputLabel id="tournamentType">Tournament Type</InputLabel>
          <Select
            labelId="tournamentType"
            id="tournamentType"
            value={filters.tournamentType ? filters.tournamentType : ""}
            onChange={handleTournamentTypeChange}
          >
            <MenuItem value={""}></MenuItem>
            <MenuItem value={"College"}>College</MenuItem>
            <MenuItem value={"Open"}>Open</MenuItem>
          </Select>
        </FormControl>
        <FormControl className={classes.narrowFormControl}>
          <InputLabel id="setType">Set Type</InputLabel>
          <Select
            labelId="setType"
            id="setType"
            value={!!filters.setType === filters.setType ? filters.setType : ""}
            onChange={handleSetTypeChange}
          >
            <MenuItem value={""}></MenuItem>
            <MenuItem value={false}>All-Subject</MenuItem>
            <MenuItem value={true}>Subject, Vanity, or Trash</MenuItem>
          </Select>
        </FormControl>
      </div>
    </div>
  );
}
